import React from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Robot,
  User,
  BookmarkSimple,
  ListPlus,
  FileText,
  Warning,
} from '@phosphor-icons/react';

/**
 * Message component for Copilot chat
 */
const CopilotMessage = ({ message, onSuggestedAction }) => {
  const isUser = message.type === 'user';
  const isError = message.type === 'error';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isUser
            ? 'bg-slate-200'
            : isError
            ? 'bg-red-100'
            : 'bg-primary/10'
        }`}
      >
        {isUser ? (
          <User size={16} className="text-slate-600" />
        ) : (
          <Robot size={16} className={isError ? 'text-red-600' : 'text-primary'} />
        )}
      </div>

      {/* Message Content */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        <div
          className={`inline-block rounded-2xl px-4 py-2.5 text-sm ${
            isUser
              ? 'bg-primary text-white rounded-br-md'
              : isError
              ? 'bg-red-50 text-red-800 rounded-bl-md'
              : 'bg-slate-100 text-slate-800 rounded-bl-md'
          }`}
        >
          {/* Main message text */}
          <div className="whitespace-pre-wrap">{message.content}</div>

          {/* Clarification questions */}
          {message.questions && message.questions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
              {message.questions.map((q, i) => (
                <div key={q.id || i} className="text-left">
                  <p className="text-xs font-medium text-slate-500 mb-1">
                    Question {i + 1}:
                  </p>
                  <p className="text-sm">{q.text}</p>
                  {q.options && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {q.options.map((opt, j) => (
                        <Badge key={j} variant="outline" className="text-xs cursor-pointer hover:bg-slate-200">
                          {opt}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Citations */}
          {message.data?.citations && message.data.citations.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-500 mb-1">Sources:</p>
              <div className="flex flex-wrap gap-1">
                {message.data.citations.map((cite, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {cite.source || cite.bookTitle} - {cite.section}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Next best action */}
          {message.data?.next_best_action && (
            <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2 text-xs text-primary">
              <Warning size={14} />
              <span>{message.data.next_best_action}</span>
            </div>
          )}
        </div>

        {/* Suggested Actions */}
        {message.suggested_actions && message.suggested_actions.length > 0 && !isUser && (
          <div className="flex flex-wrap gap-2 mt-2">
            {message.suggested_actions.map((action, i) => (
              <Button
                key={i}
                variant="outline"
                size="sm"
                onClick={() => onSuggestedAction(action)}
                className="text-xs h-7 gap-1"
                data-testid={`suggested-action-${action.type}`}
              >
                {action.type === 'SAVE_PROPOSED_LEARNING' && <BookmarkSimple size={12} />}
                {action.type === 'ADD_BACKLOG' && <ListPlus size={12} />}
                {action.type === 'GENERATE_REPORT' && <FileText size={12} />}
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Message list component for Copilot panel
 */
export const CopilotMessageList = ({ messages, onSuggestedAction, loading }) => {
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.length === 0 ? (
        <div className="text-center py-8">
          <Robot size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">
            Hi! I'm your Copilot assistant.
          </p>
          <p className="text-slate-400 text-xs mt-1">
            Ask me anything or use the quick actions above.
          </p>
        </div>
      ) : (
        messages.map((message, index) => (
          <CopilotMessage
            key={index}
            message={message}
            onSuggestedAction={onSuggestedAction}
          />
        ))
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="flex gap-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Robot size={16} className="text-primary" />
          </div>
          <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CopilotMessageList;
